import React, { memo } from 'react';

import {
  ProductTile,
  ProductTileContent,
  ProductDetails,
  ProductTileInfoButton,
  ProductTitle,
  ProductImage,
  ProductSubtitle,
  ProductTileCtaSection
} from '@shared-components/molecules/ProductTile';
import Button from '@shared-components/atoms/Button';
import Badge from '@shared-components/atoms/Badge';

import clsx from 'clsx';
import { useDecision } from '@optimizely/react-sdk';
import { useSelector } from 'react-redux';
import css from './DealTile.module.css';
import CountdownTimer from '@/common/countdownTimer/CountdownTimer';
import { Availability } from '@/menu/pizza/pizzaMenuTypes';
import { localizationSelectors } from '@/localization/localizationSelectors';
import { getTimerDetails } from '@/common/countdownTimer/getTimerDetails';

export type DealTileData = {
  id: string;
  name: string;
  imageAltText?: string;
  description?: string;
  outOfStock: boolean;
  imageURL?: string | null;
  formattedPrice: string | null;
  primaryCTA: {
    text: string;
    onClick: () => void;
    isLoading: boolean;
  };
  badges: {
    new?: boolean;
    soldOut: boolean;
  };
  availability?: Availability[];
};

type DealTileProps = {
  deal: DealTileData;
  index: number;
};

type HardcodedDescriptionVariables = {
  deals?: {
    data?: {
      id?: string;
      description?: string;
    }[];
  };
}

export const DealTile = memo(({
  deal,
  index
}: DealTileProps): JSX.Element => {
  const occasion = useSelector(localizationSelectors.occasion);
  const storeDetails = useSelector(localizationSelectors.storeDetails);
  const storeTimezone = storeDetails?.storeTimezone;
  const {
    name,
    imageAltText,
    description,
    outOfStock,
    imageURL,
    formattedPrice,
    primaryCTA,
    badges,
    availability
  } = deal;

  const [hardCodedDescriptionDecision] = useDecision('cb-ims-650-hardcode-additional-deal-description');
  const [{enabled: dealsCTACopyChangeBuildDealEnabled}]= useDecision('fr-ims-609-deals-page-cta-copy-change');

  const hardCodedDescriptionData = hardCodedDescriptionDecision?.enabled
    ? (hardCodedDescriptionDecision.variables as HardcodedDescriptionVariables)?.deals?.data ?? []
    : [];

  const isInStock = !outOfStock;
  const { duration, intervalStartTime, isAvailable } = getTimerDetails(availability, occasion, storeTimezone);

  // If the deal is not available after given duration for time sensitive deals, hide deal tile.
  if(!isAvailable) {
    return <></>
  }

  // Needs a longterm solution
  const dealTileClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const list = (e.target as HTMLElement).classList;

    if (!list.contains('MuiDrawer-paper') 
      && !list.contains('MuiModal-backdrop') 
      && !list.contains('MuiTypography-root') 
      && !list.contains('MuiGrid-container')
      && !list.contains('MuiGrid-root')
      && !list.contains('MuiBox-root')
      && !(e.target as HTMLElement).nodeName.toLowerCase().includes('path')) {
      primaryCTA.onClick();
    }
  }
  let completeDescription = description;
  const hardcodedDescription = hardCodedDescriptionData.find(item => item.id === deal.id)?.description || '';
  if (hardCodedDescriptionDecision?.enabled && hardcodedDescription) {
    completeDescription = description + (hardcodedDescription ? ` ${hardcodedDescription}` : '');
  }

  return (
    <ProductTile testId='deal-tile' data-tile-index={index + 1} className={css['deal-tile__container']}>
        {isAvailable && duration && intervalStartTime && (
          <div className={css['deal-tile__mobile-countdown']}>
            <CountdownTimer duration={duration} intervalStartTime={intervalStartTime} $variant="deals" dealName={name}/>
          </div>
        )}
        <ProductTileContent
          data-testid='deal-tile-content'
          className={clsx(css['deal-tile__content'], outOfStock && css['deal-tile__content--oos'])}
          onClick={outOfStock || primaryCTA.isLoading ? undefined : dealTileClick}
        >  
          <ProductImage
            className={css['deal-tile__product-image']}
            imageURL={imageURL ?? ''}
            imageAltText={imageAltText || name}
            productName={name}
          />

          <ProductDetails className={css['deal-tile__product-details']}>
            <div className={css['deal-tile__deal-name-and-info-icon']}>
              <div>
                {badges.new && isInStock && <Badge variant="primary" className={css['deal-tile__badge']}>New</Badge>}
                <ProductTitle className={clsx(css['deal-tile__title'], outOfStock ? css['deal-tile__title--oos'] : undefined)}>
                  {name}
                </ProductTitle>
              </div>
              {isInStock && name && completeDescription && (
                <ProductTileInfoButton productName={name} productDescription={completeDescription} />
              )}
            </div>
            {badges.soldOut && (<Badge variant="disabled" className={css['deal-tile__badge']}>Sold out</Badge>)}
            {formattedPrice && (
              <ProductSubtitle className={css['deal-tile__price']}>
                {(dealsCTACopyChangeBuildDealEnabled
                  ? primaryCTA.text === 'Build Deal'
                  : primaryCTA.text === 'Get Started'
                ) && 'Starting at '}
                {formattedPrice}
              </ProductSubtitle>
            )}
            {isAvailable && duration && intervalStartTime && (
              <div className={css['deal-tile__desktop-countdown']}>
                <CountdownTimer duration={duration} intervalStartTime={intervalStartTime} $variant="deals" dealName={name}/>
              </div>
            )}
          </ProductDetails>
          {
            isInStock && (
              <ProductTileCtaSection className={css['deal-tile__cta-section']}>
                <Button
                  className={css['deal-tile__get-started-cta']}
                  variant='secondary'
                  data-testid='deal-tile-cta'
                  aria-label={`${primaryCTA.text} ${name}`}
                  disabled={outOfStock}
                  isLoading={primaryCTA.isLoading}
                >
                  {primaryCTA.text}
                </Button>
              </ProductTileCtaSection>
            )
          }
        </ProductTileContent>
    </ProductTile>
  );
});
